import { Check, ChevronRight, Flame, Timer, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDuration } from "@/lib/nutrition";
import { ChoiceMeal } from "@/components/kit/ChoiceMeal";
import { KIT } from "@/constants/testIds";

const Stat = ({ icon: Icon, value, tone = "soft" }) =>
  value ? (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 text-[12.5px] font-bold",
        tone === "kcal" ? "text-brand-orangeInk" : "text-brand-teal/55"
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {value}
    </span>
  ) : null;

/** 1:1 recipe imagery beside the dish, then the actions. `card` stands alone; `row` sits in a card. */
export const MealTile = ({ meal, completed = false, locked = false, variant = "row", onToggle, onOpenRecipe }) => (
  <article
    data-testid={KIT.mealTile(meal.slot)}
    className={cn(variant === "card" && "ps-card ps-card-hover p-4 sm:p-5")}
  >
    <div className="group flex gap-4 sm:gap-5">
      <div
        className={cn(
          "ps-photo relative aspect-square w-[84px] shrink-0 self-start rounded-[16px] min-[381px]:w-[92px] sm:w-[104px]",
          completed && "ring-2 ring-brand-green/40",
          onOpenRecipe && meal.recipe_id && "ps-tap cursor-pointer"
        )}
        onClick={onOpenRecipe && meal.recipe_id ? () => onOpenRecipe(meal) : undefined}
        data-testid={meal.recipe_id ? KIT.mealTileImage(meal.slot) : undefined}
      >
        {meal.image ? (
          <img
            src={meal.image}
            alt=""
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.06]"
          />
        ) : (
          <div
            className="grid h-full w-full place-items-center font-display text-2xl font-bold text-white/85"
            style={{ backgroundColor: "#17403B" }}
          >
            {meal.title?.[0]}
          </div>
        )}
        {completed && (
          <span className="ps-pop absolute bottom-1.5 right-1.5 grid h-7 w-7 place-items-center rounded-full bg-brand-green text-white shadow-card">
            <Check className="h-4 w-4" strokeWidth={3} />
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <h3
          className={cn(
            "font-display text-[16.5px] font-bold leading-snug text-brand-teal sm:text-[17.5px]",
            completed && "text-brand-teal/55",
            onOpenRecipe && meal.recipe_id && "ps-tap cursor-pointer hover:text-brand-orangeInk"
          )}
          data-testid={meal.recipe_id ? KIT.mealTileTitle(meal.slot) : undefined}
          onClick={onOpenRecipe && meal.recipe_id ? () => onOpenRecipe(meal) : undefined}
        >
          {meal.title}
        </h3>
        {meal.description && (
          <p className="mt-1 text-[13.5px] leading-[1.45] text-brand-teal/60">{meal.description}</p>
        )}

        <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1">
          <Stat icon={Flame} value={meal.calories} tone="kcal" />
          <Stat icon={Zap} value={meal.protein} />
          <Stat icon={Timer} value={formatDuration(meal.prep_time)} />
        </div>
      </div>
    </div>

    {/* The choice grid gets the full width of the meal card, so every option is easy to scan. */}
    {!!meal.options?.length && (
      <ChoiceMeal
        slot={meal.slot}
        choose={meal.choose}
        options={meal.options}
        onOpenRecipe={onOpenRecipe}
      />
    )}

    <div className="mt-4 flex flex-wrap items-center gap-2.5">
      {meal.recipe_id && !meal.options?.length && (
        <button
          data-testid={KIT.mealTileRecipe(meal.slot)}
          onClick={() => onOpenRecipe?.(meal)}
          className="ps-tap inline-flex min-h-[42px] items-center gap-1.5 rounded-full border border-brand-teal/20 bg-white px-4 text-[13px] font-bold text-brand-teal transition-colors hover:border-brand-teal/45 hover:text-brand-orangeInk"
        >
          View Recipe <ChevronRight className="h-3.5 w-3.5" />
        </button>
      )}
      <button
        data-testid={KIT.mealTileToggle(meal.slot)}
        aria-pressed={completed}
        disabled={locked}
        onClick={() => onToggle?.(meal)}
        className={cn(
          "ps-tap inline-flex min-h-[42px] items-center gap-1.5 rounded-full px-4 text-[13px] font-bold transition-colors disabled:opacity-50",
          completed
            ? "bg-brand-tealDeep text-white hover:bg-brand-tealInk"
            : "bg-brand-tealTint text-brand-tealDeep hover:bg-brand-mint"
        )}
      >
        <Check className="h-3.5 w-3.5" strokeWidth={completed ? 3 : 2.4} />
        {completed ? "Eaten" : "Mark as Eaten"}
      </button>
    </div>
  </article>
);
